import { useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactForm from "@/components/ContactForm";
import AnimatedSection from "@/components/AnimatedSection";
import { useLang } from "@/i18n/LangContext";
import { Building2, Home, Building, Ruler, ClipboardCheck, Hammer, ShieldCheck } from "lucide-react";

const HERO_BG = "https://customer-assets.emergentagent.com/job_84c34016-99d1-4711-81d8-4592c54a5a5e/artifacts/glvnzv3i_1000368636.jpg";
const GALLERY = [
  { url: "https://customer-assets.emergentagent.com/job_84c34016-99d1-4711-81d8-4592c54a5a5e/artifacts/gyzqj0u3_1000368628.jpg", titleKey: "gallery1Title", descKey: "gallery1Desc" },
  { url: "https://customer-assets.emergentagent.com/job_84c34016-99d1-4711-81d8-4592c54a5a5e/artifacts/sv1cw3ao_1000368632.jpg", titleKey: "gallery2Title", descKey: "gallery2Desc" },
  { url: "https://customer-assets.emergentagent.com/job_84c34016-99d1-4711-81d8-4592c54a5a5e/artifacts/glvnzv3i_1000368636.jpg", titleKey: "gallery3Title", descKey: "gallery3Desc" },
  { url: "https://customer-assets.emergentagent.com/job_84c34016-99d1-4711-81d8-4592c54a5a5e/artifacts/0bf2gk10_1000368638.jpg", titleKey: "gallery4Title", descKey: "gallery4Desc" },
];

export default function ConstructionPage() {
  const { t } = useLang();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const SERVICES = [
    { icon: Home, titleKey: "constService1Title", descKey: "constService1Desc" },
    { icon: Building, titleKey: "constService2Title", descKey: "constService2Desc" },
    { icon: Building2, titleKey: "constService3Title", descKey: "constService3Desc" },
  ];

  const PROCESS = [
    { num: "01", titleKey: "constProcess1Title", descKey: "constProcess1Desc" },
    { num: "02", titleKey: "constProcess2Title", descKey: "constProcess2Desc" },
    { num: "03", titleKey: "constProcess3Title", descKey: "constProcess3Desc" },
    { num: "04", titleKey: "constProcess4Title", descKey: "constProcess4Desc" },
  ];

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="construction-page">
      <Navbar />
      {/* Hero */}
      <section className="relative h-screen flex items-end overflow-hidden" data-testid="construction-hero">
        <motion.div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/40 to-black/20" />
        <div className="relative z-10 px-6 sm:px-12 lg:px-24 pb-20 sm:pb-28 max-w-5xl">
          <motion.span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#C8A951] font-body block mb-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}>{t("constHeroOverline")}</motion.span>
          <motion.h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl text-white font-light tracking-tight leading-[1.1]" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            {t("constHeroTitle1")}<br /><span className="text-[#C8A951]">{t("constHeroTitle2")}</span>
          </motion.h1>
          <motion.div className="h-[2px] bg-[#C8A951] w-16 mt-6" initial={{ width: 0 }} animate={{ width: 64 }} transition={{ delay: 0.9, duration: 0.6 }} />
        </div>
      </section>

      {/* About */}
      <section className="py-24 sm:py-32 lg:py-40 px-6 sm:px-12 lg:px-24" data-testid="construction-about">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <AnimatedSection>
            <span className="text-xs uppercase tracking-[0.3em] text-[#C8A951] font-body block mb-4">{t("constAboutOverline")}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-normal tracking-tight mb-6">{t("constAboutTitle")}</h2>
            <div className="h-[2px] bg-[#C8A951] w-12 mb-8" />
            <p className="text-[#A1A1AA] font-body text-base sm:text-lg leading-relaxed mb-6">{t("constAboutP1")}</p>
            <p className="text-[#A1A1AA] font-body text-base sm:text-lg leading-relaxed">{t("constAboutP2")}</p>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Ruler, val: "98%", key: "respectDelais" },
                { icon: ShieldCheck, val: "95%", key: "satisfactionClient" },
                { icon: Hammer, val: "92%", key: "qualiteMat" },
                { icon: ClipboardCheck, val: "99%", key: "securiteChantier" },
              ].map((s, i) => (
                <div key={i} className="bg-[#1A1A1A] border border-white/5 p-8 flex flex-col items-center text-center">
                  <s.icon className="text-[#C8A951] w-8 h-8 mb-3" strokeWidth={1} />
                  <span className="font-heading text-3xl text-white">{s.val}</span>
                  <span className="text-[#A1A1AA] text-xs tracking-[0.15em] uppercase mt-2 font-body">{t(s.key)}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24 bg-[#0a0a0a]" data-testid="construction-services">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <span className="text-xs uppercase tracking-[0.3em] text-[#C8A951] font-body block mb-4">{t("constServicesOverline")}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-normal tracking-tight mb-4">{t("constServicesTitle")}</h2>
            <div className="h-[2px] bg-[#C8A951] w-12 mb-16" />
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SERVICES.map((service, i) => (
              <AnimatedSection key={i} delay={i * 0.15}>
                <div className="bg-[#1A1A1A] border border-white/5 p-8 sm:p-10 group hover:border-[#C8A951]/30 transition-all duration-500 h-full" data-testid={`construction-service-${i}`}>
                  <service.icon className="w-10 h-10 text-[#C8A951] mb-6 group-hover:scale-110 transition-transform duration-300" strokeWidth={1} />
                  <h3 className="font-heading text-xl sm:text-2xl text-white mb-4">{t(service.titleKey)}</h3>
                  <p className="text-[#A1A1AA] text-sm sm:text-base font-body leading-relaxed">{t(service.descKey)}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 sm:py-32 lg:py-40 px-6 sm:px-12 lg:px-24" data-testid="construction-gallery">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <span className="text-xs uppercase tracking-[0.3em] text-[#C8A951] font-body block mb-4">{t("constGalleryOverline")}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-normal tracking-tight mb-4">{t("constGalleryTitle")}</h2>
            <div className="h-[2px] bg-[#C8A951] w-12 mb-16" />
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {GALLERY.map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className={`relative overflow-hidden group cursor-pointer ${i === 0 ? "md:row-span-2 h-[300px] md:h-full" : "h-[300px]"}`} data-testid={`gallery-item-${i}`}>
                  <img src={item.url} alt={t(item.titleKey)} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                    <h3 className="font-heading text-white text-lg">{t(item.titleKey)}</h3>
                    <p className="text-[#C8A951] text-sm font-body">{t(item.descKey)}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24 bg-[#0a0a0a]" data-testid="construction-process">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <span className="text-xs uppercase tracking-[0.3em] text-[#C8A951] font-body block mb-4">{t("constProcessOverline")}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-normal tracking-tight mb-4">{t("constProcessTitle")}</h2>
            <div className="h-[2px] bg-[#C8A951] w-12 mb-16" />
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((step, i) => (
              <AnimatedSection key={i} delay={i * 0.15}>
                <div className="relative" data-testid={`construction-process-${i}`}>
                  <span className="font-heading text-6xl sm:text-7xl text-[#C8A951]/10 absolute -top-4 -left-2">{step.num}</span>
                  <div className="pt-12 pl-2">
                    <h3 className="font-heading text-lg sm:text-xl text-white mb-3">{t(step.titleKey)}</h3>
                    <p className="text-[#A1A1AA] text-sm font-body leading-relaxed">{t(step.descKey)}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 sm:py-32 lg:py-40 px-6 sm:px-12 lg:px-24" data-testid="construction-contact">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-[0.3em] text-[#C8A951] font-body block mb-4">{t("constContactOverline")}</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-normal tracking-tight mb-4">{t("constContactTitle")}</h2>
              <p className="text-[#A1A1AA] font-body max-w-lg mx-auto">{t("constContactDesc")}</p>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.2}><ContactForm source="construction" /></AnimatedSection>
        </div>
      </section>
      <Footer />
    </div>
  );
}
